import React, { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';

import WysiwygEditor from 'src/modules/shared/components/WysiwygEditor/WysiwygEditor';
import { useTranslation } from 'react-i18next';
import { ContentTitleWrapper, StyledDialog, StyledDialogActions, StyledForm } from './styled';
import { StyledSpriteIcon } from 'src/modules/notes/components/Notes.styles';
import { SpriteIconsSolid } from 'src/config/api/models/FontAwesome';
import { StudyCard } from 'src/modules/shared/slices/study-card/types/cards';
import useKeyBindings from 'src/modules/shared/hooks/useKeyBindings';
import { useSnackbar } from 'notistack';
import {
  StudyCardsTypes,
  usePostAddStudyCardsMutation,
  usePatchStudyCardsMutation,
  useDeleteStudyCardsMutation,
  useGetStudyCardsQuery,
  PostAddStudyCards,
  PatchtudyCards,
} from 'src/modules/shared/slices/study-card/api/studyCard';
import { ConfirmationDeleteCardDialog } from './ConfirmationDeleteCardDialog';
import { useGetPagesQuery } from 'src/modules/shared/slices/page/api/pages';
import { trajectorySectionCodeLookup } from 'src/config/api/models/Trajectory';

export type AddEditDeleteCardProps = {
  type: StudyCardsTypes;
  onClose: () => void;
  mode: 'add' | 'edit' | null;
};

type CardFormProperties = {
  title: string;
  name: 'question' | 'answer' | 'optionalExplanation';
  isValid: boolean;
  isRequired: boolean;
  value?: string;
};

const CardsContent: CardFormProperties[] = [
  {
    title: 'Voorkant',
    name: 'question',
    isValid: false,
    isRequired: true,
    value: '',
  },
  {
    title: 'Achterkant',
    name: 'answer',
    isValid: false,
    isRequired: true,
    value: '',
  },
  {
    title: 'Optionele toevoeging',
    name: 'optionalExplanation',
    isValid: true,
    isRequired: false,
    value: '',
  },
];

export const AddEditDeleteCard: FC<AddEditDeleteCardProps> = ({ type, onClose, mode }) => {
  const log = useDataLogging();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const currentCardIndex = useAppSelector((state) => state.studyCard.currentPreviewCardIndex);
  const trajectory = useAppSelector((state) => state.trajectories.trajectory);
  const [addStudyCards, addCardsResponse] = usePostAddStudyCardsMutation();
  const [patchStudyCards, patchCardsResponse] = usePatchStudyCardsMutation();
  const [deleteStudyCards, deleteCardsResponse] = useDeleteStudyCardsMutation();
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [formSubmited, setFormSubmited] = useState<boolean>(false);
  const [formContent, setFormContent] = useState<CardFormProperties[]>(CardsContent);
  const { data: pageData } = useGetPagesQuery({
    trajectoryId: trajectory?.id ?? '',
    type: trajectorySectionCodeLookup['3'],
  });
  const pageId = pageData?.find((item) => item.type === trajectorySectionCodeLookup['3'])?.id;

  const isLoading =
    addCardsResponse.isLoading || patchCardsResponse.isLoading || deleteCardsResponse.isLoading;

  useKeyBindings({
    Escape: () => {
      onClose();
    },
  });

  const { data: cards } = useGetStudyCardsQuery(
    {
      cardType: type,
      pageId: pageId as string,
    },
    { skip: !pageId }
  );

  const updateCardsContent = useCallback(() => {
    if (!cards) return;
    const response = cards[currentCardIndex];
    const keyMapping: Record<string, keyof StudyCard> = {
      question: 'question',
      answer: 'answer',
      optionalExplanation: 'optionalExplanation',
    };

    setFormContent((prev) => {
      const updatedFormContent = prev.map((item) => {
        const responseKey = keyMapping[item.name];
        const newValue = response[responseKey] as string;

        return {
          ...item,
          value: newValue ? newValue : '',
          isValid: item.isRequired && newValue ? newValue.length > 0 : true,
        };
      });

      return updatedFormContent;
    });
  }, [cards, currentCardIndex]);

  useEffect(() => {
    if (mode === 'add') return;
    if (!cards) return;
    if (!deleteCardsResponse.isUninitialized) return;
    updateCardsContent();
  }, [mode, cards, updateCardsContent, deleteCardsResponse.isUninitialized]);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setFormSubmited(true);
      if (formContent.some((item) => item.isValid === false)) return;

      let formData: { question: string; answer: string; optionalExplanation?: string } = {
        question: '',
        answer: '',
        optionalExplanation: '',
      };
      formContent.forEach((item) => {
        formData = { ...formData, ...{ [item.name]: item.value } };
      });

      const body: PostAddStudyCards = {
        ...formData,
        pageId: pageId as string,
        cardType: type as StudyCardsTypes,
      };

      if (mode === 'edit' && cards) {
        const patchBody: PatchtudyCards = {
          ...body,
          studyCardId: cards[currentCardIndex].id,
          optionalExplanation: body.optionalExplanation === '' ? null : body.optionalExplanation,
        };
        log(LogActionTypes.STUDYCARD_EDIT_CARD, {
          studyCardId: cards[currentCardIndex].id,
          pageId,
        });
        patchStudyCards(patchBody);
        return;
      } else {
        log(LogActionTypes.STUDYCARD_ADD_CARD, {
          pageId,
        });
        addStudyCards(body);
      }
    },
    [addStudyCards, cards, currentCardIndex, formContent, log, mode, pageId, patchStudyCards, type]
  );

  const handleDeleteStudyCard = useCallback(() => {
    if (!cards || !pageId) return;
    log(LogActionTypes.STUDYCARD_DELETE_CARD, {
      studyCardId: cards[currentCardIndex].id,
      pageId,
    });
    deleteStudyCards({ studyCardId: cards[currentCardIndex].id, pageId, cardType: type });
  }, [cards, pageId, log, currentCardIndex, deleteStudyCards, type]);

  useEffect(() => {
    if (addCardsResponse.isSuccess) {
      enqueueSnackbar('New Card added', {
        variant: 'success',
      });
      onClose();
      addCardsResponse.reset();
    }
  }, [addCardsResponse, enqueueSnackbar, onClose]);

  useEffect(() => {
    if (patchCardsResponse.isSuccess) {
      enqueueSnackbar('Card updated', {
        variant: 'success',
      });
      onClose();
      patchCardsResponse.reset();
    }
  }, [patchCardsResponse, onClose, enqueueSnackbar]);

  useEffect(() => {
    if (deleteCardsResponse.isSuccess) {
      enqueueSnackbar('Card successfully deleted', {
        variant: 'success',
      });
      onClose();
      deleteCardsResponse.reset();
    }
  }, [deleteCardsResponse, enqueueSnackbar, onClose]);

  useEffect(() => {
    if (deleteCardsResponse.isError || patchCardsResponse.isError || addCardsResponse.isError) {
      enqueueSnackbar('Something went wrong... Please try again later', {
        variant: 'error',
      });
    }
  }, [addCardsResponse.isError, deleteCardsResponse, enqueueSnackbar, patchCardsResponse.isError]);

  return (
    <>
      <StyledDialog
        disableEnforceFocus
        open={mode !== null}
        scroll="paper"
        style={{ padding: '0', overflowY: 'hidden' }}
      >
        <StyledForm onSubmit={handleSubmit}>
          <DialogTitle>{`Kaartje ${mode === 'add' ? ' toevoegen' : 'bewerken'}`}</DialogTitle>
          <DialogContent>
            {formContent.map((item, index) => {
              return (
                <>
                  <ContentTitleWrapper>
                    <Typography variant="body1" style={{ fontWeight: 700 }}>
                      {item.title}
                    </Typography>
                    {item.isRequired && <Typography variant="caption">verplicht</Typography>}
                  </ContentTitleWrapper>
                  <WysiwygEditor
                    type="normal"
                    id={item.name}
                    toolbarSticky={false}
                    value={item.value ? item.value : ''}
                    className={formSubmited && !item.isValid ? 'form-error' : ''}
                    handleBlur={(content: string) => {
                      setFormContent((prev: CardFormProperties[]) => {
                        const newContent = [...prev];
                        newContent[index] = {
                          ...newContent[index],
                          value: content,
                          isValid: content.length > 0,
                        };
                        return newContent;
                      });
                    }}
                  />
                </>
              );
            })}
          </DialogContent>

          <StyledDialogActions>
            <div>
              {mode === 'edit' && (
                <Button
                  variant="outlined"
                  onClick={() => setShowDeleteDialog(true)}
                  style={{ height: '36px' }}
                  startIcon={
                    <StyledSpriteIcon icon={SpriteIconsSolid.TRASH} color={'red'} iconSize="md" />
                  }
                >
                  verwijderen
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
              <Button
                variant="text"
                onClick={() => onClose()}
                style={{ height: '36px' }}
                disabled={isLoading}
              >
                {t('shared.forms.cancel')}
              </Button>
              <Button
                style={{ height: '36px' }}
                size="small"
                disabled={isLoading}
                color="primary"
                variant="contained"
                type="submit"
                onClick={() => {}}
                disableElevation={true}
              >
                {'BEWAAR'}
              </Button>
            </div>
          </StyledDialogActions>
        </StyledForm>
      </StyledDialog>
      {showDeleteDialog && (
        <ConfirmationDeleteCardDialog
          open={showDeleteDialog}
          onConfirm={handleDeleteStudyCard}
          onCancel={() => setShowDeleteDialog(false)}
        />
      )}
    </>
  );
};
